import { z } from "zod";

// US State codes validation
const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
  "AS",
  "FM",
  "GU",
  "MH",
  "MP",
  "PW",
  "PR",
  "VI",
] as const;

// Phone number validation (US format)
const phoneRegex =
  /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;

// ZIP code validation (5 digits or 5+4 format)
const zipCodeRegex = /^[0-9]{5}(-[0-9]{4})?$/;

// Email validation (more permissive than default)
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reusable validation schemas for address and contact information
 */
export const validationSchemas = {
  // Basic text fields
  name: z
    .string()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters"),
  optionalName: z
    .string()
    .max(100, "Name must be less than 100 characters")
    .optional(),

  // Contact information
  email: z
    .string()
    .min(1, "Email is required")
    .refine(
      (email) => emailRegex.test(email),
      "Please enter a valid email address",
    ),

  phone: z
    .string()
    .refine(
      (phone) => !phone || phoneRegex.test(phone),
      "Please enter a valid US phone number",
    )
    .optional(),

  // Address fields
  streetAddress: z
    .string()
    .max(255, "Street address must be less than 255 characters")
    .optional(),

  streetAddress2: z
    .string()
    .max(255, "Street address line 2 must be less than 255 characters")
    .optional(),

  city: z.string().max(100, "City must be less than 100 characters").optional(),

  state: z
    .string()
    .min(1, "State is required")
    .refine(
      (state) =>
        US_STATES.includes(state.toUpperCase() as (typeof US_STATES)[number]),
      "Please enter a valid US state code (e.g., CA, NY, TX)",
    )
    .transform((state) => state.toUpperCase()),

  zipCode: z
    .string()
    .min(1, "ZIP code is required")
    .refine(
      (zip) => zipCodeRegex.test(zip),
      "Please enter a valid ZIP code (12345 or 12345-6789)",
    ),

  country: z
    .string()
    .max(100, "Country must be less than 100 characters")
    .default("US")
    .optional(),
};

/**
 * Complete address validation schema
 * Use this when you need full address validation
 */
export const addressSchema = z.object({
  streetAddress: validationSchemas.streetAddress,
  streetAddress2: validationSchemas.streetAddress2,
  city: validationSchemas.city,
  state: validationSchemas.state,
  zipCode: validationSchemas.zipCode,
  country: validationSchemas.country,
});

/**
 * Contact information validation schema
 * Use this when you need email/phone validation
 */
export const contactSchema = z.object({
  email: validationSchemas.email,
  phone: validationSchemas.phone,
});

/**
 * Full person schema with name, contact, and address
 * Use this for customer/contact forms
 */
export const personSchema = z.object({
  firstName: validationSchemas.name,
  lastName: validationSchemas.name,
  ...contactSchema.shape,
  ...addressSchema.shape,
});

/**
 * Utility function to format phone numbers consistently
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, "");

  // Handle different formats
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits[0] === "1") {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return phone; // Return original if we can't format it
};

/**
 * Utility function to format addresses consistently
 */
export const formatAddress = (address: {
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}): string => {
  const parts: string[] = [];

  if (address.streetAddress) {
    parts.push(address.streetAddress);
  }

  if (address.streetAddress2) {
    parts.push(address.streetAddress2);
  }

  const cityStateZip = [address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(" ");

  if (cityStateZip) {
    parts.push(cityStateZip);
  }

  if (address.country && address.country !== "US") {
    parts.push(address.country);
  }

  return parts.join(", ");
};

/**
 * US States list for dropdowns
 */
export const US_STATES_LIST = US_STATES.map((code) => ({
  value: code,
  label: code,
}));

// Type exports
export type AddressType = z.infer<typeof addressSchema>;
export type ContactType = z.infer<typeof contactSchema>;
export type PersonType = z.infer<typeof personSchema>;
